import { z } from 'zod'

/**
 * Categorias de servicio disponibles
 */
export const CATEGORIAS_SERVICIO = [
  'impuestos_nacionales',
  'impuestos_provinciales',
  'sueldos',
  'estados_contables',
  'inscripciones',
  'asesoramiento',
  'otros',
] as const

export type CategoriaServicio = (typeof CATEGORIAS_SERVICIO)[number]

/**
 * Labels legibles para las categorias
 */
export const CATEGORIA_SERVICIO_LABELS: Record<CategoriaServicio, string> = {
  impuestos_nacionales: 'Impuestos Nacionales',
  impuestos_provinciales: 'Impuestos Provinciales',
  sueldos: 'Liquidacion de Sueldos',
  estados_contables: 'Estados Contables',
  inscripciones: 'Inscripciones y Tramites',
  asesoramiento: 'Asesoramiento',
  otros: 'Otros',
}

/**
 * Opciones para selects de categoria
 */
export const CATEGORIAS_SERVICIO_OPTIONS = CATEGORIAS_SERVICIO.map((cat) => ({
  value: cat,
  label: CATEGORIA_SERVICIO_LABELS[cat],
}))

/**
 * Estados de servicio disponibles
 */
export const ESTADOS_SERVICIO = ['activo', 'inactivo'] as const
export type EstadoServicio = (typeof ESTADOS_SERVICIO)[number]

/**
 * Labels legibles para los estados
 */
export const ESTADO_SERVICIO_LABELS: Record<EstadoServicio, string> = {
  activo: 'Activo',
  inactivo: 'Inactivo',
}

/**
 * Opciones para selects de estado
 */
export const ESTADOS_SERVICIO_OPTIONS = ESTADOS_SERVICIO.map((estado) => ({
  value: estado,
  label: ESTADO_SERVICIO_LABELS[estado],
}))

/**
 * Periodicidades disponibles
 */
export const PERIODICIDADES_SERVICIO = [
  'mensual',
  'bimestral',
  'trimestral',
  'semestral',
  'anual',
  'unico',
] as const

export type PeriodicidadServicio = (typeof PERIODICIDADES_SERVICIO)[number]

/**
 * Labels legibles para las periodicidades
 */
export const PERIODICIDAD_SERVICIO_LABELS: Record<PeriodicidadServicio, string> = {
  mensual: 'Mensual',
  bimestral: 'Bimestral',
  trimestral: 'Trimestral',
  semestral: 'Semestral',
  anual: 'Anual',
  unico: 'Unico',
}

/**
 * Opciones para selects de periodicidad
 */
export const PERIODICIDADES_SERVICIO_OPTIONS = PERIODICIDADES_SERVICIO.map((p) => ({
  value: p,
  label: PERIODICIDAD_SERVICIO_LABELS[p],
}))

/**
 * Schema Zod para formulario de Servicio
 */
export const servicioSchema = z.object({
  nombre: z
    .string()
    .min(1, 'El nombre es requerido')
    .min(2, 'El nombre debe tener al menos 2 caracteres')
    .max(100, 'El nombre no puede exceder 100 caracteres'),

  categoria: z.enum(CATEGORIAS_SERVICIO, {
    error: 'Selecciona una categoria',
  }),

  descripcion: z
    .string()
    .max(500, 'La descripcion no puede exceder 500 caracteres')
    .nullable()
    .optional()
    .transform((val) => val?.trim() || null),

  precio_estimado: z
    .number()
    .min(0, 'El precio no puede ser negativo')
    .nullable()
    .optional(),

  periodicidad: z.enum(PERIODICIDADES_SERVICIO, {
    error: 'Selecciona una periodicidad',
  }),

  estado: z.enum(ESTADOS_SERVICIO).default('activo'),

  aplica_persona_fisica: z.boolean().default(true),

  aplica_persona_juridica: z.boolean().default(true),

  aplica_sucesion_indivisa: z.boolean().default(true),
})

/**
 * Tipo inferido del schema
 */
export type ServicioFormValues = z.infer<typeof servicioSchema>

/**
 * Valores por defecto para crear un servicio nuevo
 */
export const DEFAULT_SERVICIO_VALUES: ServicioFormValues = {
  nombre: '',
  categoria: 'otros',
  descripcion: null,
  precio_estimado: null,
  periodicidad: 'mensual',
  estado: 'activo',
  aplica_persona_fisica: true,
  aplica_persona_juridica: true,
  aplica_sucesion_indivisa: true,
}
